
import { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { JobFormValues } from "../schemas/jobFormSchema";
import { JobDocumentUpload } from "./form/JobDocumentUpload";
import { JobBasicInfo } from "./form/JobBasicInfo";
import { JobDetailedInfo } from "./form/JobDetailedInfo";
import { AIContentGenerator } from "./form/AIContentGenerator";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface JobFormFieldsProps {
  form: UseFormReturn<JobFormValues>;
  clientName?: string;
}

export const JobFormFields = ({ form, clientName }: JobFormFieldsProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractionError, setExtractionError] = useState<string | null>(null);

  const processJobDocument = async (url: string, type: 'pdf' | 'url') => {
    try {
      setIsProcessing(true);
      toast.info("Processing job document...");
      
      console.log(`Processing ${type} document: ${url}`);
      
      const { data, error } = await supabase.functions.invoke('process-job-document', {
        body: { documentUrl: url, sourceType: type }
      });
      
      if (error) throw error;
      
      console.log('Received job details:', data);
      
      if (!data || Object.keys(data).length === 0) {
        throw new Error('No job details extracted');
      }
      
      let fieldsPopulated = 0;
      let descriptionPopulated = false;
      let requirementsPopulated = false;
      
      // Process each field from the extracted data
      Object.entries(data).forEach(([key, value]) => {
        if (value && key in form.getValues() && value !== "") {
          console.log(`Setting form field ${key} to:`, value);
          
          // Fix for the job type enum error
          if (key === 'type') {
            // First, normalize the job type value
            const normalizedType = typeof value === 'string' 
              ? value.toString().toLowerCase().replace(/\s+/g, '-')
              : '';
              
            // Check if the normalized value is one of our valid enum values
            const validTypes = ['full-time', 'part-time', 'contract', 'remote'];
            
            if (validTypes.includes(normalizedType)) {
              // Type assertion to tell TypeScript this is a valid enum value
              form.setValue(key, normalizedType as "full-time" | "part-time" | "contract" | "remote", {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true
              });
              fieldsPopulated++;
            } else {
              console.log(`Invalid job type: ${normalizedType}, defaulting to full-time`);
              // Default to full-time if the value doesn't match any valid type
              form.setValue(key, 'full-time', {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true
              });
              fieldsPopulated++;
            }
          } else {
            // For all other fields
            form.setValue(key as keyof JobFormValues, value as string, {
              shouldValidate: true,
              shouldDirty: true,
              shouldTouch: true
            });
            fieldsPopulated++;
          }
          
          if (key === 'description') descriptionPopulated = true;
          if (key === 'requirements') requirementsPopulated = true;
        }
      });
      
      if (descriptionPopulated && requirementsPopulated) {
        toast.success('Job details fully extracted!');
      } else if (fieldsPopulated >= 3) {
        toast.success('Basic job details extracted - please review and complete any missing fields');
      } else if (fieldsPopulated > 0) {
        toast.warning('Limited job details extracted - please complete the remaining fields');
      } else {
        toast.error('Failed to extract meaningful job details');
      }
    } catch (error) {
      console.error('Error processing document:', error);
      toast.error('Failed to extract job details');
      setExtractionError('Failed to extract job details. Please fill in the fields manually.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <JobDocumentUpload 
        form={form} 
        onProcess={processJobDocument}
        isProcessing={isProcessing}
      />
      <JobBasicInfo form={form} />
      
      <AIContentGenerator form={form} clientName={clientName} />
      
      <JobDetailedInfo form={form} />
    </>
  );
};
